import { useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router";
import { useStaffWorkspace } from "../../../app/session";
import { api } from "../../../data";
import { ErrorState, Loading, NoAccess } from "../../../components/States";
import { PillView, useAction } from "../../../components/ui";
import { date } from "../../../lib/format";
import {
  createTalent, findTalentDuplicates, inviteNewCreator, inviteTalent, resendInvitation, revokeInvitation, type Duplicate, type InvitationResult,
} from "./data";
import { useBase, useInvitationDays, usePlan } from "./hooks";
import {
  accessOf, aktiveCreatorImTarif, bpLabel, creatorBelegungText, emailValid, invitationLink, invitationPill, invitationState, nameOf, percentToBp, splitName,
  type InvitationState, type TalentFull,
} from "./model";
import { ConfirmAction, CopyLink, DemoNote, FieldError, RIGHTS_TEXT } from "./parts";
import { GrenzeWachsen } from "../../../components/Tarif";
import { t, ta } from "../../../lib/sprache";
import { creatorGrenzeText, testFreiText } from "../../../model/tarif";

/**
 * Creator einladen (11_app_agentur/einladen.html): invitation_create, offene Einladungen neu senden oder zurückziehen (AD-074, AD-077).
 * Vor dem Anlegen wird nach demselben Menschen gesucht (gleiche E-Mail, auch archiviert, oder gleicher Name).
 */

type Done = { title: string; talent: TalentFull | null; email: string | null; invitation: InvitationResult | null };

export function CreatorEinladen() {
  const ws = useStaffWorkspace();
  const orgId = ws.org.org_id;
  const tz = ws.org.timezone;
  const { base, error, loading, noAccess, retry } = useBase(ws);
  const days = useInvitationDays(orgId);
  const plan = usePlan(ws);
  const { busy, error: actionError, run } = useAction();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [commission, setCommission] = useState("");
  const [language, setLanguage] = useState("");
  const [errors, setErrors] = useState<{ name?: string; email?: string; commission?: string }>({});
  const [dups, setDups] = useState<{ list: Duplicate[]; mode: "einladen" | "anlegen" } | null>(null);
  const [done, setDone] = useState<Done | null>(null);
  const [showAll, setShowAll] = useState(false);

  const invitations = useMemo(() => {
    if (!base) return [];
    return base.invitations.map((i) => ({ i, state: invitationState(i, base.nowMs), talent: i.talent_id ? base.talentById.get(i.talent_id) ?? null : null }));
  }, [base]);
  const withoutAccess = useMemo(() => {
    if (!base) return [];
    return base.talents
      .filter((t) => t.status !== "archived")
      .map((t) => ({ t, access: accessOf(t.id, base.links, base.invitations, base.nowMs) }))
      .filter((x) => x.access.state === "none" || x.access.state === "expired" || x.access.state === "revoked")
      .sort((a, b) => nameOf(a.t).localeCompare(nameOf(b.t), "de"));
  }, [base]);

  if (error) return <ErrorState error={error} retry={retry} />;
  if (noAccess) return <NoAccess />;
  if (loading || !base) return <Loading />;

  const restricted = !ws.isChef && !ws.membership.sees_all_talents;
  // Leer = Agentur-Standard (folgt späteren Änderungen des Standards); nur eine Eingabe setzt einen eigenen Satz
  const commissionValue = commission;
  const validDays = days.data ?? 14;
  // K-592: Beispiel-Creator zählen nicht in den Tarif (wie die Datenbank)
  const active = aktiveCreatorImTarif(base.talents);
  const atLimit = !!plan.data && plan.data.maxTalents >= 0 && active >= plan.data.maxTalents;
  const linkOf = (r: InvitationResult) => invitationLink(window.location.origin, r.token);

  function validate(requireEmail: boolean): boolean {
    const e: typeof errors = {};
    if (!splitName(name).first) e.name = t("Bitte einen Namen angeben.", "Please enter a name.");
    if (requireEmail && !email.trim()) e.email = t("Bitte eine E-Mail-Adresse angeben.", "Please enter an email address.");
    else if (email.trim() && !emailValid(email)) e.email = t("Bitte eine gültige E-Mail-Adresse angeben.", "Please enter a valid email address.");
    if (percentToBp(commissionValue) === undefined) e.commission = t("Bitte eine Zahl zwischen 0 und 100 angeben.", "Please enter a number between 0 and 100.");
    setErrors(e);
    return Object.keys(e).length === 0;
  }
  function reset() {
    setName("");
    setEmail("");
    setCommission("");
    setLanguage("");
    setErrors({});
    setDups(null);
  }
  /** Gibt es den Menschen schon? Dann erst nachfragen (Umsätze und Verträge sollen nicht auf zwei Profile zerfallen). */
  async function checkDuplicates(mode: "einladen" | "anlegen"): Promise<boolean> {
    const { first, last } = splitName(name);
    const list = await findTalentDuplicates(api, orgId, { email, first, last }).catch(() => [] as Duplicate[]);
    if (list.length) setDups({ list, mode });
    return list.length > 0;
  }
  async function invite(e?: FormEvent, allowDuplicate = false) {
    e?.preventDefault();
    if (!validate(true)) return;
    if (!allowDuplicate && (await checkDuplicates("einladen"))) return;
    const r = await run("einladen", () => inviteNewCreator(api, orgId, { name, email, commission: commissionValue, language, allowDuplicate }), t("Einladung erstellt.", "Invitation created."));
    if (r) {
      setDone({ title: t(`Einladung für ${nameOf(r.talent)} erstellt.`, `Invitation for ${nameOf(r.talent)} created.`), talent: r.talent, email: r.talent.email, invitation: r.invitation });
      reset();
    }
  }
  async function createOnly(allowDuplicate = false) {
    if (!validate(false)) return;
    if (!allowDuplicate && (await checkDuplicates("anlegen"))) return;
    const { first, last } = splitName(name);
    const tl = await run("anlegen", () => createTalent(api, orgId, {
      first_name: first, last_name: last, display_name: name.trim().replace(/\s+/g, " "), nickname: null, email: email.trim().toLowerCase() || null,
      instagram_handle: null, tiktok_handle: null, youtube_handle: null, whatsapp_phone: null, street: null, postal_code: null, city: null, country: null,
      vat_id: null, tax_number: null, small_business: false, commission_bp: percentToBp(commissionValue) ?? null, language: language || null,
    }, "active", { allowDuplicate }), t("Creator angelegt.", "Creator created."));
    if (tl) {
      setDone({ title: t(`${nameOf(tl)} ist angelegt, ohne Portal-Zugang.`, `${nameOf(tl)} has been created, without portal access.`), talent: tl, email: null, invitation: null });
      reset();
    }
  }
  async function inviteExisting(tl: TalentFull) {
    const r = await run(`inv-${tl.id}`, () => inviteTalent(api, tl.id), t("Einladung erstellt.", "Invitation created."));
    if (r) setDone({ title: t(`Einladung für ${nameOf(tl)} erstellt.`, `Invitation for ${nameOf(tl)} created.`), talent: tl, email: tl.email, invitation: r });
  }
  async function resend(id: string, tl: TalentFull | null, to: string) {
    const r = await run(`neu-${id}`, () => resendInvitation(api, id), t("Einladung neu gesendet.", "Invitation resent."));
    if (r) setDone({ title: t(`Einladung${tl ? t(` für ${nameOf(tl)}`, ` for ${nameOf(tl)}`) : ""} neu gesendet. Der bisherige Link gilt nicht mehr.`, `Invitation${tl ? t(` für ${nameOf(tl)}`, ` for ${nameOf(tl)}`) : ""} resent. The previous link no longer works.`), talent: tl, email: to, invitation: r });
  }

  const listed = invitations.filter((x) => showAll || x.state === "open" || x.state === "expired");
  const stateOrder: Record<InvitationState, number> = { open: 0, expired: 1, revoked: 2, accepted: 3 };
  listed.sort((a, b) => stateOrder[a.state] - stateOrder[b.state] || b.i.created_at.localeCompare(a.i.created_at));

  return (
    <main id="main" className="content tk-sd">
      <div className="pagehead">
        <div>
          <div className="eyebrow">{ta("Agentur-Arbeitsbereich", "Agency workspace", "Dein Creator-Konto", "Your creator account")}</div>
          <h1>{t("Creator anlegen", "Add creator")}</h1>
          <p></p>
        </div>
      </div>

      {done && (
        <div className="notice stack" role="status">
          <p>
            <strong>{done.title}</strong>
            {done.invitation && t(` Die E-Mail an ${done.email ?? t("den Creator", "the creator")} geht über den Postausgang raus; der Link ist bis ${date(done.invitation.expires_at, tz)} gültig.`, ` The email to ${done.email ?? t("den Creator", "the creator")} goes out via the outbox; the link is valid until ${date(done.invitation.expires_at, tz)}.`)}
            {done.invitation && api.mode === "demo" && t(" In der Demo werden keine E-Mails verschickt.", " In the demo no emails are sent.")}
          </p>
          {done.invitation && <CopyLink label={t("Einladungslink zum Weitergeben", "Invitation link to pass on")} url={linkOf(done.invitation)} />}
          <div className="row">
            {done.talent && <Link className="btn" to={`/agentur/creator/${done.talent.id}`}>{t("Profil öffnen", "Open profile")}</Link>}
            <button className="" onClick={() => setDone(null)}>{t("Hinweis schließen", "Close notice")}</button>
          </div>
        </div>
      )}

      <form className="panel stack" onSubmit={(e) => void invite(e)} noValidate>
        <label>
          Name
          <input id="name" type="text" value={name} onChange={(e) => { setName(e.target.value); setDups(null); }} autoComplete="off" required
            aria-invalid={errors.name ? true : undefined} aria-describedby={errors.name ? "name-fehler" : undefined} />
          <FieldError id="name-fehler" text={errors.name} />
        </label>
        <label>
          {t("E-Mail", "Email")}<input id="email" type="email" value={email} onChange={(e) => { setEmail(e.target.value); setDups(null); }} autoComplete="off" required
            aria-invalid={errors.email ? true : undefined} aria-describedby={errors.email ? "email-fehler" : undefined} />
          <FieldError id="email-fehler" text={errors.email} />
        </label>
        <label>
          {t("Provision in Prozent", "Commission in percent")}<input id="provisioninprozent" type="number" min={0} max={100} step={0.5} value={commissionValue.replace(",", ".")} onChange={(e) => setCommission(e.target.value)}
            placeholder={t(`Agentur-Standard ${bpLabel(base.settings.default_commission_bp ?? 0)}`, `Agency default ${bpLabel(base.settings.default_commission_bp ?? 0)}`)}
            aria-invalid={errors.commission ? true : undefined} aria-describedby={errors.commission ? "provision-fehler" : "provision-hinweis"} />
          <small id="provision-hinweis">{t("Agenturanteil. Leer = Agentur-Standard (folgt späteren Änderungen des Standards); ein Satz an der Kooperation geht vor.", "Agency share. Empty = agency default (follows later changes of the default); a rate on the deal takes precedence.")}</small>
          <FieldError id="provision-fehler" text={errors.commission} />
        </label>
        <label>
          {t("Sprache der Einladung", "Invitation language")}<select id="sprache" value={language} onChange={(e) => setLanguage(e.target.value)}>
            <option value="">{t("Wie die Agentur", "Same as the agency")}</option>
            <option value="de">{t("Deutsch", "German")}</option>
            <option value="en">{t("Englisch", "English")}</option>
          </select>
        </label>
        <p>{t("Der Einladungslink ist ", "The invitation link is valid for ")}{validDays === 1 ? t("einen Tag", "one day") : t(`${validDays} Tage`, `${validDays} days`)} {t("gültig. Die Einladung kann widerrufen werden.", ". The invitation can be revoked.")}</p>
        {plan.data && (
          <p className={atLimit ? "tk-error" : undefined} role={atLimit ? "alert" : undefined}>
            {creatorBelegungText(plan.data, active)}
            {/* K-377: einmal, was nach dem Test gilt */}
            {plan.data.testBis && <> {testFreiText(plan.data.testBis, plan.data.planName, creatorGrenzeText(plan.data.planMaxTalents))}.</>}{atLimit && <> {t("Neue Creator lassen sich erst nach dem Archivieren eines Creators oder mit mehr Creator-Plätzen anlegen.", "New creators can only be created after archiving a creator or with more creator slots.")}<GrenzeWachsen art="talents" /></>}
          </p>
        )}
        {restricted && <p>{t("Neue Creator werden dir automatisch zugewiesen, weil deine Sicht auf zugewiesene Creator beschränkt ist.", "New creators are automatically assigned to you because your view is limited to assigned creators.")}</p>}
        {dups && (
          <div className="notice stack" role="alert">
            <p><strong>{t("Gibt es den Creator schon?", "Does the creator already exist?")}</strong> {t("In deiner Agentur gibt es ", "In your agency there is ")}{dups.list.length === 1 ? t("ein Profil", "one profile") : t(`${dups.list.length} Profile`, `${dups.list.length} profiles`)} {t("mit dieser E-Mail-Adresse oder diesem Namen:", "with this email address or name:")}</p>
            <ul>
              {dups.list.map((d, i) => (
                <li key={d.id ?? `unsichtbar-${i}`}>
                  {d.id ? <Link to={`/agentur/creator/${d.id}`}>{d.name}</Link> : t("ein Profil, das für dein Konto nicht sichtbar ist (frag deinen Admin)", "a profile not visible to your account (ask your admin)")}
                  {d.status === "archived" ? t(" · archiviert", " · archived") : ""} {t("· gleiche ", "· same ")}{d.reason === "email" ? t("E-Mail-Adresse", "email address") : "Name"}
                </li>
              ))}
            </ul>
            <p>{t("Nutze das vorhandene Profil (reaktivieren oder einladen), damit Kooperationen, Verträge und Umsätze nicht auf zwei Profile zerfallen.", "Use the existing profile (reactivate or invite) so that deals, contracts and revenue are not split across two profiles.")}</p>
            <div className="row">
              <button type="button" disabled={!!busy} onClick={() => void (dups.mode === "einladen" ? invite(undefined, true) : createOnly(true))}>{t("Trotzdem neu anlegen", "Create new anyway")}</button>
              <button type="button" onClick={() => setDups(null)}>{t("Abbrechen", "Cancel")}</button>
            </div>
          </div>
        )}
        {actionError && <p className="tk-error" role="alert">{actionError}</p>}
        <div className="row">
          <button className="primary" type="submit" disabled={!!busy} aria-busy={busy === "einladen"}>{busy === "einladen" ? t("Wird gespeichert …", "Saving …") : t("Einladung erstellen", "Create invitation")}</button>
          <button className="" type="button" disabled={!!busy} onClick={() => void createOnly()}>{busy === "anlegen" ? t("Wird gespeichert …", "Saving …") : t("Ohne Einladung anlegen", "Create without invitation")}</button>
        </div>
      </form>

      <h2>{t("Einladungen", "Invitations")}</h2>
      <div className="toolbar">
        <button className="" aria-pressed={!showAll} onClick={() => setShowAll(false)}>{t("Offen · ", "Open · ")}{invitations.filter((x) => x.state === "open" || x.state === "expired").length}</button>
        <button className="" aria-pressed={showAll} onClick={() => setShowAll(true)}>{t("Alle · ", "All · ")}{invitations.length}</button>
      </div>
      <div className="tablewrap">
        <table>
          <caption className="sr-only">{t("Einladungen an Creator", "Invitations to creators")}</caption>
          <thead>
            <tr>
              <th scope="col">{t("E-Mail", "Email")}</th>
              <th scope="col">Creator</th>
              <th scope="col">{t("Eingeladen am", "Invited on")}</th>
              <th scope="col">{t("Gültig bis", "Valid until")}</th>
              <th scope="col">Status</th>
              <th scope="col"><span className="sr-only">{t("Aktionen", "Actions")}</span></th>
            </tr>
          </thead>
          <tbody>
            {listed.map(({ i, state, talent }) => {
              const linked = talent ? base.links.some((l) => l.talent_id === talent.id) : false;
              // Neu senden nur für offene oder abgelaufene Einladungen; zurückgezogene bleiben zurückgezogen (neuer Link = neue Einladung)
              const resendable = (state === "open" || state === "expired") && !linked && talent?.status !== "archived";
              return (
                <tr key={i.id}>
                  <td>{i.email}</td>
                  <td>{talent ? <Link to={`/agentur/creator/${talent.id}`}>{nameOf(talent)}</Link> : "–"}</td>
                  <td>{date(i.created_at, tz)}</td>
                  <td>{state === "accepted" ? t(`angenommen am ${date(i.accepted_at, tz)}`, `accepted on ${date(i.accepted_at, tz)}`) : state === "revoked" ? t(`zurückgezogen am ${date(i.revoked_at, tz)}`, `withdrawn on ${date(i.revoked_at, tz)}`) : date(i.expires_at, tz)}</td>
                  <td><PillView pill={invitationPill(state)} /></td>
                  <td>
                    <div className="row">
                      {resendable && (
                        <button className="" disabled={!!busy} onClick={() => void resend(i.id, talent, i.email)}>{busy === `neu-${i.id}` ? t("Wird gesendet …", "Sending …") : t("Neu senden", "Resend")}</button>
                      )}
                      {state === "open" && (
                        <ConfirmAction label={t("Zurückziehen", "Withdraw")} confirmLabel={t("Ja, zurückziehen", "Yes, withdraw")} busy={busy === `weg-${i.id}`}
                          question={t(`Einladung an ${i.email} zurückziehen? Der Link funktioniert danach nicht mehr.`, `Withdraw invitation to ${i.email}? The link will then no longer work.`)}
                          onConfirm={() => run(`weg-${i.id}`, () => revokeInvitation(api, i.id), t("Einladung zurückgezogen.", "Invitation withdrawn."))} />
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {listed.length === 0 && <tr><td colSpan={6}>{showAll ? t("Noch keine Einladungen.", "No invitations yet.") : t("Keine offenen Einladungen.", "No open invitations.")}</td></tr>}
          </tbody>
        </table>
      </div>

      <h2>{t("Creator ohne Zugang", "Creators without access")}</h2>
      <div className="tablewrap">
        <table>
          <caption className="sr-only">{t("Creator ohne Portal-Zugang", "Creators without portal access")}</caption>
          <thead>
            <tr>
              <th scope="col">Creator</th>
              <th scope="col">{t("E-Mail", "Email")}</th>
              <th scope="col">{t("Zugang", "Access")}</th>
              <th scope="col"><span className="sr-only">{t("Aktionen", "Actions")}</span></th>
            </tr>
          </thead>
          <tbody>
            {withoutAccess.map(({ t: tl, access }) => (
              <tr key={tl.id}>
                <td><Link to={`/agentur/creator/${tl.id}`}><strong>{nameOf(tl)}</strong></Link></td>
                <td>{tl.email ?? <span className="muted">{t("Keine E-Mail hinterlegt", "No email on file")}</span>}</td>
                <td><PillView pill={access.pill} /></td>
                <td>
                  <button className="" disabled={!tl.email || !!busy} aria-describedby={tl.email ? undefined : `ohne-mail-${tl.id}`} onClick={() => void inviteExisting(tl)}>
                    {busy === `inv-${tl.id}` ? t("Wird gesendet …", "Sending …") : t("Einladen", "Invite")}
                  </button>
                  {!tl.email && <small id={`ohne-mail-${tl.id}`}>{t("Erst eine E-Mail im Profil hinterlegen.", "First enter an email in the profile.")}</small>}
                </td>
              </tr>
            ))}
            {withoutAccess.length === 0 && <tr><td colSpan={4}>{t("Alle sichtbaren Creator haben einen Zugang oder eine offene Einladung.", "All visible creators have access or an open invitation.")}</td></tr>}
          </tbody>
        </table>
      </div>
      <DemoNote />
    </main>
  );
}
